import React from 'react'
import { useSelector } from 'react-redux'
import Row from '@amzn/meridian/row'
import Column from '@amzn/meridian/column'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import geopinTokens from '@amzn/meridian-tokens/base/icon/geopin'
import { useBundle } from '@amzn/react-arb-tools'
import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { formatRelativeTime } from '@src/pages/NotificationsPage/utils'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { IActionCenterNotification } from '../types'
import { OPEN_DETAILS, PRIORITY_LABEL, UNREAD } from '../constants'
import { isRead } from '../utils'
import { CategoryIcon } from './CategoryIcon'
import { useCategoryLabel } from './CategoryAccordion'
import { resolvePrimaryAction } from '../resolveAction'

interface INotificationCardProps {
    notification: IActionCenterNotification
    /** The store the user is currently in. Actions only route when the card belongs to it. */
    currentStoreId?: string
    /** False on the view-only "All stores" tab: the producer's action is not rendered at all. */
    showActions?: boolean
    onOpen: (notification: IActionCenterNotification) => void
}

/**
 * One inbox card: severity-toned category icon, title, summary line, details line, location,
 * relative time, and (on the Current store tab) the producer's primary action.
 *
 * Accessibility: the card itself is not a control. The title is the button that opens the detail
 * view (so the producer's action button is a sibling, never a focusable descendant of another
 * button), and it is described by a visually hidden line spelling out what colour and the dot
 * convey to sighted users: priority, category, and whether the notification is still unread.
 * Tapping anywhere else on the card also opens the detail view, as a pointer convenience.
 */
export const NotificationCard: React.FC<INotificationCardProps> = ({ notification, currentStoreId, showActions = true, onOpen }) => {
    const locale = useSelector(getSelectedLocale)
    const [bundle] = useBundle(BUNDLE_NAME)
    const categoryLabel = useCategoryLabel()
    const action = showActions ? resolvePrimaryAction(notification, currentStoreId) : undefined
    const summary = notification.summary ?? notification.body
    const unread = !isRead(notification)
    const stateId = `action-center-card-state-${notification.notificationId}`
    const state = [
        t(bundle, PRIORITY_LABEL[notification.priority]),
        categoryLabel(notification.category ?? ''),
        unread ? t(bundle, UNREAD) : '',
    ].filter(Boolean).join(', ')

    const open = () => onOpen(notification)
    const openFromTitle = (e: React.MouseEvent) => {
        e.stopPropagation() // the card's own onClick would open it a second time
        open()
    }

    return (
        <div
            className="action-center-card"
            data-testid="action-center-card"
            data-notification-id={notification.notificationId}
            onClick={open}
        >
            <Row alignmentVertical="top" spacing="300" widths={['fit', 'fill']}>
                {/* Decorative: the icon and dot are spelled out in the title's description. */}
                <div className="action-center-card__icon" aria-hidden="true">
                    {unread && <span className="action-center-card__unread-dot" data-testid="unread-dot" />}
                    <CategoryIcon category={notification.category} priority={notification.priority} />
                </div>
                <Column spacing="100">
                    <button
                        type="button"
                        className="action-center-card__open"
                        data-testid="card-open"
                        aria-label={t(bundle, OPEN_DETAILS, { title: notification.title })}
                        aria-describedby={stateId}
                        onClick={openFromTitle}
                    >
                        <Text type="h100" tag="span" className="action-center-card__title">{notification.title}</Text>
                    </button>
                    <span id={stateId} className="action-center-card__state" data-testid="card-state">{state}</span>
                    {summary && (
                        <Text type="b200" color="secondary" className="action-center-card__summary" data-testid="card-summary">
                            {summary}
                        </Text>
                    )}
                    {notification.details && (
                        <Text type="b200" color="secondary" data-testid="card-details">{notification.details}</Text>
                    )}
                    {notification.location && (
                        <Row spacing="100" alignmentVertical="center">
                            <Icon tokens={geopinTokens} />
                            <Text type="b200" color="secondary" data-testid="card-location">{notification.location}</Text>
                        </Row>
                    )}
                    <Row alignmentHorizontal="end" alignmentVertical="center" spacing="300">
                        <Text type="b100" color="secondary" data-testid="card-time">
                            {formatRelativeTime(notification.createdAt, bundle, locale)}
                        </Text>
                        {action && (
                            // Meridian Button does not hand the click event to onClick, so the
                            // bubble to the card (which would open the detail view) is stopped here.
                            <span className="action-center-card__action" onClick={(e) => e.stopPropagation()}>
                                <Button type="primary" size="small" data-testid="card-action" disabled={!action.enabled} onClick={action.navigate}>
                                    {action.label}
                                </Button>
                            </span>
                        )}
                    </Row>
                </Column>
            </Row>
        </div>
    )
}
